import moment from "moment-timezone";


const parseBreakdown = (breakdownParam, campaigns) => {
  if (!breakdownParam) return [];
  try {
    const parsedBreakdown = JSON.parse(decodeURIComponent(breakdownParam));

    return parsedBreakdown.map((item) => {
      if (item.fundId) {
        const specificCampaign = campaigns.find(
          (c) =>
            c.fundId === item.fundId
        );

        if (specificCampaign) {
          return {
            campaignId: specificCampaign.campaignId,
            amount: item.amount
          };
        }

        const defaultCampaign = campaigns.find((c) =>
          c.name.toLowerCase().includes("where most")
        );

        if (defaultCampaign) {
          return {
            campaignId: defaultCampaign.campaignId,
            amount: item.amount
          };
        }
      }

      return item;
    });
  } catch {
    return [];
  }
};

const calculateTotalAmount = (breakdown) =>{
  return breakdown.reduce((sum, item) => sum + item.amount, 0)}

const calculateProcessingFee = (donationAmount) =>
  (donationAmount * 0.015 + 0.20).toFixed(2);

const organizeDonations = (breakdown, schedule) => {
  const totalBreakdownAmount = breakdown.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  if (totalBreakdownAmount === 0) return [];

  const weightedBreakdown = breakdown.map((item) => ({
    campaignId: item.campaignId,
    weight: item.amount / totalBreakdownAmount
  }));

  return schedule.map((day) => {
    let remainingAmount = day.amount;
    const allocations = weightedBreakdown.map((campaign, index) => {
      const allocatedAmount =
        index === weightedBreakdown.length - 1
          ? remainingAmount
          : parseFloat((day.amount * campaign.weight).toFixed(2));
      remainingAmount = parseFloat(
        (remainingAmount - allocatedAmount).toFixed(2)
      );
      return {
        campaignId: campaign.campaignId,
        amount: allocatedAmount
      };
    });

    return {
      date: day.date,
      allocations
    };
  });
};

const parseSchedule = (scheduleParam) => {
  if (!scheduleParam) return [];
  try {
    return JSON.parse(decodeURIComponent(scheduleParam));
  } catch {
    return [];
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  return `${weekday} ${day} ${month}`;
};

/**
 * Calculates the breakdown of funds for Friday donations
 * 
 * @param {Array} selectedFunds - Array of selected fund IDs
 * @param {number} weeklyAmount - Weekly donation amount
 * @returns {Array} - Breakdown array with fundId and calculated amounts
 */
const scheduleBreakdown = (selectedFunds, weeklyAmount) => {
  if (selectedFunds.length === 0 || !weeklyAmount || weeklyAmount < 10) {
    return [];
  }
  
  const currentDate = new Date();
  const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
  let fridaysLeft = 0;
  
  // Count Fridays left in the year
  const tempDate = new Date(currentDate);
  while (tempDate <= endOfYear) {
    if (tempDate.getDay() === 5) {
      fridaysLeft++;
    }
    tempDate.setDate(tempDate.getDate() + 1);
  }
  
  const totalForYear = weeklyAmount * fridaysLeft;
  const perFund = totalForYear / selectedFunds.length;
  
  return selectedFunds.map((fundId) => ({
    fundId,
    amount: perFund,
  }));
};

/**
 * Generate donation schedule with different logic based on calendar type
 * 
 * @param {Date|string} startDate - Start date for the donation schedule
 * @param {Date|string} endDate - End date for the donation schedule
 * @param {number} hourOfDonation - Hour of the day when donations should occur (0-23)
 * @param {number} interval - Interval in days between donations
 * @param {number} donationAmount - Total donation amount
 * @param {number} pz - Selected distribution option (0: Equal, 1: Custom, etc.)
 * @param {string} userTimezoneName - User's timezone name (e.g., 'America/New_York')
 * @param {string} template - Calendar template name (e.g., 'Hijri', 'Hijri10', 'Lunar', etc.)
 * @returns {Array} Array of scheduled donation days with dates and amounts
 */
const generateDonationSchedule = (
  startDate,
  endDate,
  hourOfDonation,
  interval,
  donationAmount,
  selectedOption,
  userTimezoneName,
  template
) => {
  // Validate input parameters
  if (!startDate || !hourOfDonation || !interval || !donationAmount || !userTimezoneName || selectedOption === null || selectedOption === undefined) {
    throw new Error("Invalid input parameters: One or more required parameters are null or empty.");
  }

  const now = moment().tz(userTimezoneName).startOf("day");
  const startDateLocal = moment.tz(startDate, "YYYY-MM-DD", userTimezoneName);
  if (!endDate) {
    endDate = startDateLocal.clone().add(1, "year").format("YYYY-MM-DD");
  }
  const schedule = [];
  const currentMoment = startDateLocal
    .clone()
    .hour(hourOfDonation)
    .minute(0)
    .second(0);
  const endMoment = moment
    .tz(endDate, "YYYY-MM-DD", userTimezoneName)
    .hour(hourOfDonation)
    .minute(0)
    .second(0);

  while (currentMoment.isSameOrBefore(endMoment)) {
    if (!currentMoment.isBefore(now, "day")) {
      schedule.push({
        date: currentMoment.utc().toISOString(),
        amount: 0
      });
    }
    currentMoment.add(interval, "days");
  }

  // Determine if we are in a 10-night calendar (Hijri10/Lunar10)

  const isTenNightCalendar = template === "Hijri10" || template === "Lunar10";

  const isFridayCalendar = template === "Fridays";

  if (isFridayCalendar) {
    schedule.forEach((day) => {
      day.amount = parseFloat(
        donationAmount.toFixed(2)
      );
    });
    return schedule;
  }
    // For Fridays calendar 
  if (isTenNightCalendar) {
    // For Hijri10/Lunar10 calendars
    switch (selectedOption) {
      case 0: // Equally
        schedule.forEach((day) => {
          day.amount = parseFloat(
            (donationAmount / schedule.length).toFixed(2)
          );
        });
        break;
      case 1: // Custom
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // 27th Night Only: use startDate + 6 days
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = startDateLocal
            .clone()
            .add(6, "days")
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      case 3: // Odd Nights Only
        {
          const oddNights = schedule.filter((day, i) => (i + 1) % 2 === 1);
          oddNights.forEach((day) => {
            day.amount = parseFloat(
              (donationAmount / oddNights.length).toFixed(2)
            );
          });
          schedule.forEach((day, i) => {
            if ((i + 1) % 2 === 0) day.amount = 0;
          });
        }
        break;
      case 4: // More on Odd Nights – 2/3 to odd nights, 1/3 to even nights
        {
          const oddCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 1
          ).length;
          const evenCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 0
          ).length;
          schedule.forEach((day, i) => {
            if ((i + 1) % 2 === 1) {
              day.amount = parseFloat(
                ((donationAmount * (2 / 3)) / oddCount).toFixed(2)
              );
            } else {
              day.amount = parseFloat(
                ((donationAmount * (1 / 3)) / evenCount).toFixed(2)
              );
            }
          });
        }
        break;
      default:
        break;
    }
  } else {
    // For regular calendars (Hijri, Lunar, Test)
    switch (selectedOption) {
      case 0: // Equally
        schedule.forEach((day) => {
          day.amount = parseFloat(
            (donationAmount / schedule.length).toFixed(2)
          );
        });
        break;
      case 1: // Custom
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // Last 10 Nights
        if (schedule.length >= 10) {
          const lastTen = schedule.slice(-10);
          lastTen.forEach((day) => {
            day.amount = parseFloat(
              (donationAmount / lastTen.length).toFixed(2)
            );
          });
          schedule.forEach((day, i) => {
            if (i < schedule.length - 10) day.amount = 0;
          });
        } else {
          schedule.forEach((day) => {
            day.amount = parseFloat(
              (donationAmount / schedule.length).toFixed(2)
            );
          });
        }
        break;
      case 3: // 27th Night Only: use startDate + 26 days
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = startDateLocal
            .clone()
            .add(26, "days")
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      default:
        break;
    }
  }

  return schedule;
};

/**
 * Returns the number of Fridays left in the current year
 * 
 * @returns {number} - Count of Fridays remaining in the year
 */
const getFridaysLeftInYear = () => {
  const currentDate = new Date();
  const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
  let fridaysLeft = 0;

  const tempDate = new Date(currentDate);
  while (tempDate <= endOfYear) {
    if (tempDate.getDay() === 5) {
      fridaysLeft++;
    }
    tempDate.setDate(tempDate.getDate() + 1);
  }

  return fridaysLeft;
};

export {
  parseBreakdown,
  calculateTotalAmount,
  calculateProcessingFee,
  organizeDonations,
  parseSchedule,
  formatDate,
  scheduleBreakdown,
  generateDonationSchedule,
  getFridaysLeftInYear
};
